import React, { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Icon from '@mdi/react';
import { mdiDotsHorizontal } from '@mdi/js';
import TopNavBar from '../user/top_nav_bar';
import { formatDateInLocale } from '../../services/dates';
import {
  exportMembershipsAsCsv,
  formatReferrer,
  formatStatus,
  hasActiveSubscription,
  isTestUserExpiryDate,
  PAGE_SIZE,
} from '../../services/smorg_studio_memberships';
import SmorgStudioMembershipsSetExpirationPopup from './smorg_studio_memberships_set_expiration_popup';
import SmorgStudioMembershipsChangePasswordPopup from './smorg_studio_memberships_change_password_popup';
import SmorgStudioMembershipsActionMenu from './smorg_studio_memberships_action_menu';
import SmorgStudioMembershipsDeleteUserPopup from './smorg_studio_memberships_delete_user_popup';
import SmorgStudioMembershipsChangeEmailPopup from './smorg_studio_memberships_change_email_popup';
import { userCountryCode } from '../../services/users';
import { SpaceMembershipReferrer } from '../../API';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  listSpaceMembershipsOperation,
  membershipAdminChangePasswordOperation,
  membershipAdminDeleteUserOperation,
  membershipAdminSetExpiresAtOperation,
  membershipAdminChangeEmailOperation,
  searchSpaceMembershipsOperation,
  fetchAllSmorgStudioMembershipsForExportOperation,
} from '../../operations/smorg_studio_memberships_operations';
import {
  membershipsAvailable,
  membershipUpdated,
  userDeleted,
} from '../../features/smorg_studio_memberships/smorg_studio_memberships_slice';

const POPUP_TYPE_SUBSCRIBE = 'Subscribe';
const POPUP_TYPE_CHANGE_EXPIRATION_DATE = 'Change expiration date';
const POPUP_TYPE_CHANGE_PASSWORD = 'Change password';
const POPUP_TYPE_DELETE_USER = 'Delete user';
const POPUP_TYPE_CHANGE_EMAIL = 'Change email';

const SmorgStudioMembershipsView = () => {
  const dispatch = useDispatch();

  const { data: userProfile } = useGetUserProfileQuery();

  const countryCode = userCountryCode(userProfile);

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);

  const [
    currentlyUpdatingSpaceMembershipID,
    setCurrentlyUpdatingSpaceMembershipID,
  ] = useState(null);

  const [subscriptionPopupAnchorEl, setSubscriptionPopupAnchorEl] =
    useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [knownMaxMemberships, setKnownMaxMemberships] = useState(null);

  const [isExporting, setIsExporting] = useState(false);

  const smorgStudioMemberships = useSelector(
    (state) => state.smorgStudioMemberships.smorgStudioMemberships,
  );

  const handleClickActionsMenu = (ev, membershipID) => {
    setSubscriptionPopupOpenForSpaceMembershipID(membershipID);
    setActionsMenuAnchorEl(ev.target);
  };

  const onDismissActionsMenu = () => {
    setActionsMenuAnchorEl(null);
  };

  const [
    subscriptionPopupOpenForSpaceMembershipID,
    setSubscriptionPopupOpenForSpaceMembershipID,
  ] = useState(null);

  const [subscriptionPopupType, setSubscriptionPopupType] = useState(null);

  const subscriptionPopupOpenForSpaceMembership =
    subscriptionPopupOpenForSpaceMembershipID &&
    smorgStudioMemberships.find(
      (sm) => sm.id === subscriptionPopupOpenForSpaceMembershipID,
    );

  const subscriptionPopupOpenForEmailAddress =
    subscriptionPopupOpenForSpaceMembership &&
    subscriptionPopupOpenForSpaceMembership.email;

  const onClickExport = async (ev) => {
    ev.preventDefault();
    setIsExporting(true);
    try {
      const smorgStudioMembershipsForExport =
        await fetchAllSmorgStudioMembershipsForExportOperation();
      exportMembershipsAsCsv(smorgStudioMembershipsForExport, countryCode);
    } finally {
      setIsExporting(false);
    }
  };

  const onClickSubscribe = (ev, spaceMembershipID) => {
    ev.preventDefault();
    setSubscriptionPopupOpenForSpaceMembershipID(spaceMembershipID);
    setSubscriptionPopupAnchorEl(ev.target);
    setSubscriptionPopupType(POPUP_TYPE_SUBSCRIBE);
  };

  const onClickChangeExpiration = (ev, spaceMembershipID) => {
    ev.preventDefault();
    setSubscriptionPopupOpenForSpaceMembershipID(spaceMembershipID);
    setSubscriptionPopupAnchorEl(ev.target);
    setSubscriptionPopupType(POPUP_TYPE_CHANGE_EXPIRATION_DATE);
  };

  const onClickChangePassword = (ev, spaceMembershipID) => {
    ev.preventDefault();
    setSubscriptionPopupOpenForSpaceMembershipID(spaceMembershipID);
    setSubscriptionPopupAnchorEl(ev.target);
    setSubscriptionPopupType(POPUP_TYPE_CHANGE_PASSWORD);
  };

  const onClickChangeEmail = (ev, spaceMembershipID) => {
    ev.preventDefault();
    setSubscriptionPopupOpenForSpaceMembershipID(spaceMembershipID);
    setSubscriptionPopupAnchorEl(ev.target);
    setSubscriptionPopupType(POPUP_TYPE_CHANGE_EMAIL);
  };

  const onClickDeleteUser = (ev, spaceMembershipID) => {
    ev.preventDefault();
    setSubscriptionPopupOpenForSpaceMembershipID(spaceMembershipID);
    setSubscriptionPopupAnchorEl(ev.target);
    setSubscriptionPopupType(POPUP_TYPE_DELETE_USER);
  };

  const onSetSubscriptionExpiration = async (newExpiresAt) => {
    onDismissActionsMenu();
    setSubscriptionPopupAnchorEl(null);
    setCurrentlyUpdatingSpaceMembershipID(
      subscriptionPopupOpenForSpaceMembershipID,
    );
    try {
      const newSmorgStudioMembership =
        await membershipAdminSetExpiresAtOperation(
          subscriptionPopupOpenForSpaceMembershipID,
          newExpiresAt,
        );
      dispatch(membershipUpdated(newSmorgStudioMembership));
    } finally {
      setSubscriptionPopupOpenForSpaceMembershipID(null);
      setCurrentlyUpdatingSpaceMembershipID(null);
      setSubscriptionPopupType(null);
    }
  };

  const onChangePassword = async (newPassword) => {
    onDismissActionsMenu();
    setSubscriptionPopupAnchorEl(null);
    setCurrentlyUpdatingSpaceMembershipID(
      subscriptionPopupOpenForSpaceMembershipID,
    );
    try {
      const newSmorgStudioMembership =
        await membershipAdminChangePasswordOperation(
          subscriptionPopupOpenForSpaceMembershipID,
          newPassword,
        );
      dispatch(membershipUpdated(newSmorgStudioMembership));
    } finally {
      setSubscriptionPopupOpenForSpaceMembershipID(null);
      setCurrentlyUpdatingSpaceMembershipID(null);
      setSubscriptionPopupType(null);
    }
  };

  const onChangeEmail = async (newEmailAddress) => {
    onDismissActionsMenu();
    setSubscriptionPopupAnchorEl(null);
    setCurrentlyUpdatingSpaceMembershipID(
      subscriptionPopupOpenForSpaceMembershipID,
    );
    try {
      const newSmorgStudioMembership =
        await membershipAdminChangeEmailOperation(
          subscriptionPopupOpenForSpaceMembershipID,
          newEmailAddress,
        );
      if (newSmorgStudioMembership.success) {
        dispatch(
          membershipUpdated({
            ...subscriptionPopupOpenForSpaceMembership,
            email: newEmailAddress,
          }),
        );
      }
    } finally {
      setSubscriptionPopupOpenForSpaceMembershipID(null);
      setCurrentlyUpdatingSpaceMembershipID(null);
      setSubscriptionPopupType(null);
    }
  };

  const onDeleteUser = async () => {
    onDismissActionsMenu();
    setSubscriptionPopupAnchorEl(null);
    setCurrentlyUpdatingSpaceMembershipID(
      subscriptionPopupOpenForSpaceMembershipID,
    );
    dispatch(userDeleted(subscriptionPopupOpenForSpaceMembershipID));
    try {
      await membershipAdminDeleteUserOperation(
        subscriptionPopupOpenForSpaceMembershipID,
      );
    } finally {
      setSubscriptionPopupOpenForSpaceMembershipID(null);
      setCurrentlyUpdatingSpaceMembershipID(null);
      setSubscriptionPopupType(null);
    }
  };

  const onDismissSubscriptionPopup = () => {
    setSubscriptionPopupAnchorEl(null);
    setSubscriptionPopupOpenForSpaceMembershipID(null);
    setSubscriptionPopupType(null);
  };

  const subscribeEnabled = !hasActiveSubscription(
    subscriptionPopupOpenForSpaceMembership,
  );

  const changeExpirationEnabled =
    (hasActiveSubscription(subscriptionPopupOpenForSpaceMembership) &&
      subscriptionPopupOpenForSpaceMembership.referrer ===
        SpaceMembershipReferrer.SMORG_PUBLIC_API_PROVISIONING) ||
    isTestUserExpiryDate(subscriptionPopupOpenForSpaceMembership);

  const deleteEnabled =
    !hasActiveSubscription(subscriptionPopupOpenForSpaceMembership) ||
    isTestUserExpiryDate(subscriptionPopupOpenForSpaceMembership);

  const nextOffset = (smorgStudioMemberships || []).length;

  console.log('subscriptionPopupType', subscriptionPopupType);
  const onLoadMore = useCallback(
    async (ev) => {
      ev.preventDefault();
      setIsLoading(true);
      try {
        // Load more memberships logic can be implemented here.
        const memberships = await listSpaceMembershipsOperation(
          nextOffset,
          PAGE_SIZE,
        );
        if (memberships) {
          console.log(`Loaded ${memberships.length} Smorg Studio memberships`);
          dispatch(
            membershipsAvailable({
              memberships,
              offset: nextOffset,
            }),
          );
          if (memberships.length < PAGE_SIZE) {
            setKnownMaxMemberships(nextOffset + memberships.length);
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, nextOffset],
  );

  useEffect(() => {
    if (smorgStudioMemberships.length > 0) {
      return;
    }
    setIsLoading(true);
    listSpaceMembershipsOperation(0, PAGE_SIZE)
      .then((memberships) => {
        if (memberships) {
          console.log(`Loaded ${memberships.length} Smorg Studio memberships`);
          dispatch(
            membershipsAvailable({
              memberships,
              offset: 0,
            }),
          );
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, smorgStudioMemberships.length]);

  const [currentShownPageOffset, setCurrentShownPageOffset] = useState(0);

  const [searchString, setSearchString] = useState('');

  const [membershipsBySearchString, setMembershipsBySearchString] = useState(
    [],
  );

  const [debouncedSearchString] = useDebounce(searchString, 500);

  useEffect(() => {
    if (debouncedSearchString.length === 0) {
      return;
    }
    setIsLoading(true);
    searchSpaceMembershipsOperation(debouncedSearchString)
      .then((memberships) => {
        if (memberships) {
          setMembershipsBySearchString(memberships);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [debouncedSearchString]);

  const actualSmorgStudioMemberships = debouncedSearchString
    ? membershipsBySearchString
    : smorgStudioMemberships.slice(
        currentShownPageOffset,
        currentShownPageOffset + PAGE_SIZE,
      );

  const onClickPrevious = (ev) => {
    setCurrentShownPageOffset((prevOffset) => prevOffset - PAGE_SIZE);
  };

  const onClickNext = (ev) => {
    if (currentShownPageOffset + PAGE_SIZE >= smorgStudioMemberships.length) {
      onLoadMore(ev);
    }
    setCurrentShownPageOffset((prevOffset) => prevOffset + PAGE_SIZE);
  };

  const isPreviousDisabled = currentShownPageOffset === 0;
  const isNextDisabled =
    knownMaxMemberships !== null &&
    currentShownPageOffset + PAGE_SIZE >= knownMaxMemberships;

  return (
    <>
      <TopNavBar />
      <div style={{ textAlign: 'left' }}>
        <Grid
          container
          style={{ marginTop: '20px', color: 'var(--app-primary-font-color)' }}>
          <Grid
            item
            xs={6}
            style={{
              // fontWeight: 'bold',
              fontSize: '16px',
              marginLeft: '23px',
              color: 'var(--app-primary-font-color)',
            }}>
            Your members
            <TextField
              value={searchString}
              onChange={(ev) => setSearchString(ev.target.value)}
              size="small"
              variant="standard"
              placeholder="Search by email"
              style={{ marginLeft: '40px', width: '300px' }}
              sx={{
                '& .MuiInputBase-root:before': {
                  borderBottomColor: 'white',
                },
                '& .MuiInputBase-root:after': {
                  borderBottomColor: 'white',
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                },
                '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                  color: 'white',
                },
                '& .MuiInputLabel-root': {
                  color: 'white',
                },
              }}
              InputProps={{
                style: { fontSize: '14px' },
                startAdornment: (
                  <InputAdornment position="end">
                    <IconButton tabIndex={-1} edge="start">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      edge="end"
                      onClick={() => setSearchString('')}>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3} style={{ display: 'flex' }}>
            <div style={{ margin: 'auto' }}>
              {!isLoading && !debouncedSearchString &&
                (knownMaxMemberships
                  ? `${currentShownPageOffset + 1}-${
                      currentShownPageOffset +
                      actualSmorgStudioMemberships.length
                    } of ${knownMaxMemberships}`
                  : `${currentShownPageOffset + 1}-${
                      currentShownPageOffset +
                      actualSmorgStudioMemberships.length
                    } of many`)}
            </div>
          </Grid>
          <Grid item xs={2} style={{ display: 'flex' }}>
            <div style={{ margin: 'auto' }}>
              {isExporting ? (
                <div style={{ color: 'var(--app-primary-font-color)' }}>
                  Exporting...
                </div>
              ) : (
                /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
                <a
                  href="#"
                  onClick={(ev) => {
                    if (!isExporting) {
                      onClickExport(ev);
                    }
                  }}
                  style={{ color: 'var(--app-primary-font-color)' }}>
                  Export
                </a>
              )}
            </div>
          </Grid>
        </Grid>

        <div className="smorgStudioMemberships">
          <table>
            <thead>
              <tr className="heading">
                <th>Email Address</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Created Date</th>
                <th>Expiry Date</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>
                    <div style={{ margin: '20px 0' }}>Loading...</div>
                  </td>
                </tr>
              )}
              {actualSmorgStudioMemberships.length === 0 && !isLoading && (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>
                    <div style={{ margin: '20px 0' }}>
                      No more members found.
                    </div>
                  </td>
                </tr>
              )}
              {actualSmorgStudioMemberships.map((membership) => {
                if (!membership || !membership.email) {
                  return null;
                }
                return (
                  <tr key={membership.id}>
                    <div style={{ display: 'none' }}>{membership.id}</div>
                    <td>{membership.email}</td>
                    <td>{membership.givenName}</td>
                    <td>{membership.familyName}</td>
                    <td>
                      {formatDateInLocale(membership.createdAt, countryCode)}
                    </td>
                    <td>
                      {membership.expiresAt &&
                        formatDateInLocale(membership.expiresAt, countryCode)}
                    </td>
                    <td>{formatReferrer(membership.referrer)}</td>
                    <td>{formatStatus(membership)}</td>
                    <td>
                      {membership.email &&
                        currentlyUpdatingSpaceMembershipID !==
                          membership.id && (
                          <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-controls="long-menu"
                            aria-expanded="true"
                            aria-haspopup="true"
                            size="small"
                            className="actionsPopupMenu"
                            onClick={(ev) =>
                              handleClickActionsMenu(ev, membership.id)
                            }>
                            <Icon
                              path={mdiDotsHorizontal}
                              style={{ color: 'black' }}
                            />
                          </IconButton>
                        )}
                      {currentlyUpdatingSpaceMembershipID === membership.id && (
                        <div style={{ height: '34px', display: 'flex' }}>
                          <div style={{ margin: 'auto 0' }}>Updating...</div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          margin: '0 23px',
        }}>
        <Button
          variant="outlined"
          disabled={isPreviousDisabled}
          style={{
            color: 'white',
            width: '120px',
            borderRadius: '15px',
            marginRight: '6px',
          }}
          onClick={onClickPrevious}>
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={isNextDisabled}
          style={{
            color: 'white',
            width: '120px',
            backgroundColor: isNextDisabled ? 'initial' : '#134D1D',
            borderRadius: '15px',
          }}
          onClick={onClickNext}>
          Next
        </Button>
      </div>
      {actionsMenuAnchorEl && (
        <SmorgStudioMembershipsActionMenu
          anchorEl={actionsMenuAnchorEl}
          subscribeEnabled={subscribeEnabled}
          changeExpirationEnabled={changeExpirationEnabled}
          deleteEnabled={deleteEnabled}
          onClickSubscribe={(ev) => {
            onClickSubscribe(ev, subscriptionPopupOpenForSpaceMembershipID);
          }}
          onClickChangeExpiration={(ev) => {
            onClickChangeExpiration(
              ev,
              subscriptionPopupOpenForSpaceMembershipID,
            );
          }}
          onClickChangePassword={(ev) => {
            onClickChangePassword(
              ev,
              subscriptionPopupOpenForSpaceMembershipID,
            );
          }}
          onClickChangeEmail={(ev) => {
            onClickChangeEmail(ev, subscriptionPopupOpenForSpaceMembershipID);
          }}
          onClickDelete={(ev) => {
            onClickDeleteUser(ev, subscriptionPopupOpenForSpaceMembershipID);
          }}
          onDismiss={onDismissActionsMenu}
        />
      )}
      {subscriptionPopupAnchorEl &&
        subscriptionPopupType === POPUP_TYPE_SUBSCRIBE && (
          <SmorgStudioMembershipsSetExpirationPopup
            anchorEl={subscriptionPopupAnchorEl}
            title={subscriptionPopupOpenForEmailAddress}
            prompt="Create subscription until"
            actionButtonLabel="Create"
            validateExpirationIsInTheFuture
            onSetSubscriptionExpiration={onSetSubscriptionExpiration}
            onDismiss={onDismissSubscriptionPopup}
          />
        )}
      {subscriptionPopupAnchorEl &&
        subscriptionPopupType === POPUP_TYPE_CHANGE_EXPIRATION_DATE && (
          <SmorgStudioMembershipsSetExpirationPopup
            anchorEl={subscriptionPopupAnchorEl}
            title={subscriptionPopupOpenForEmailAddress}
            prompt="Set subscription expiration date"
            actionButtonLabel="Set expiration"
            onSetSubscriptionExpiration={onSetSubscriptionExpiration}
            onDismiss={onDismissSubscriptionPopup}
          />
        )}
      {subscriptionPopupAnchorEl &&
        subscriptionPopupType === POPUP_TYPE_CHANGE_PASSWORD && (
          <SmorgStudioMembershipsChangePasswordPopup
            anchorEl={subscriptionPopupAnchorEl}
            title={subscriptionPopupOpenForEmailAddress}
            onChangePassword={onChangePassword}
            onDismiss={onDismissSubscriptionPopup}
          />
        )}
      {subscriptionPopupAnchorEl &&
        subscriptionPopupType === POPUP_TYPE_DELETE_USER && (
          <SmorgStudioMembershipsDeleteUserPopup
            anchorEl={subscriptionPopupAnchorEl}
            onDeleteUser={onDeleteUser}
            onDismiss={onDismissSubscriptionPopup}
          />
        )}
      {subscriptionPopupAnchorEl &&
        subscriptionPopupType === POPUP_TYPE_CHANGE_EMAIL && (
          <SmorgStudioMembershipsChangeEmailPopup
            anchorEl={subscriptionPopupAnchorEl}
            title={subscriptionPopupOpenForEmailAddress}
            onChangeEmail={onChangeEmail}
            onDismiss={onDismissSubscriptionPopup}
          />
        )}
    </>
  );
};

export default SmorgStudioMembershipsView;
